import { HttpClient } from '@angular/common/http';
import { Injectable, OnInit } from '@angular/core';
import { AppGlobals } from '@app/app.global';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

function createdAtValue(entity: { createdAt?: unknown; created_at?: unknown }) {
  const value = entity?.createdAt ?? entity?.created_at;
  if (value == null || value === '') {
    return '';
  }

  return String(value);
}

function compareCreatedAt(a: { createdAt?: unknown; created_at?: unknown }, b: { createdAt?: unknown; created_at?: unknown }) {
  const left = createdAtValue(a);
  const right = createdAtValue(b);

  if (left && right && left !== right) {
    return left < right ? -1 : 1;
  }

  if (left && !right) {
    return -1;
  }

  if (!left && right) {
    return 1;
  }

  return 0;
}

/** Company order stays as given. Entities under each company are oldest first. */
function sortEntitiesWithinCompanies<T extends { group?: string; createdAt?: unknown; created_at?: unknown }>(entities: T[]) {
  const groups = new Map<string, T[]>();

  entities.forEach(entity => {
    const key = entity.group ?? '';
    const bucket = groups.get(key);

    if (bucket) {
      bucket.push(entity);
    } else {
      groups.set(key, [entity]);
    }
  });

  const sorted: T[] = [];

  groups.forEach(bucket => {
    bucket.sort(compareCreatedAt);
    sorted.push(...bucket);
  });

  return sorted;
}

@Injectable({
  providedIn: 'root'
})
export class EntityService implements OnInit {
  entitiesSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  entitiesSubject$ = this.entitiesSubject.asObservable();
  entitiesSubjectTDC: BehaviorSubject<{ id: string, name: string }[]> = new BehaviorSubject<{ id: string, name: string }[]>([]);
  entitiesSubjectTDC$ = this.entitiesSubjectTDC.asObservable();
  selectedEntities: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  selectedEntities$ = this.selectedEntities.asObservable();
  singleSelectedEntity: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  singleSelectedEntity$ = this.singleSelectedEntity.asObservable();

  singleDropdownCompanies: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  singleDropdownCompanies$ = this.singleDropdownCompanies.asObservable();

  singleSelectedCompany: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  singleSelectedCompany$ = this.singleSelectedCompany.asObservable();

  selectedEntitiesTDC: BehaviorSubject<{ id: string, name: string }[]> = new BehaviorSubject<{ id: string, name: string }[]>([]);
  selectedEntitiesTDC$ = this.selectedEntitiesTDC.asObservable();
  entitiesChanges: BehaviorSubject<{ isEntityChanged: boolean }> = new BehaviorSubject<{ isEntityChanged: boolean }>({ isEntityChanged: false });
  entitiesChanges$ = this.entitiesChanges.asObservable();
  entitiesChangesTDC: BehaviorSubject<{ isEntityChanged: boolean }> = new BehaviorSubject<{ isEntityChanged: boolean }>({ isEntityChanged: false });
  entitiesChangesTDC$ = this.entitiesChangesTDC.asObservable();

  public dataSubject = new BehaviorSubject<any[]>([]);
  public companies$: Observable<any[]> = this.dataSubject.asObservable();

  private dataSubjectProductTags = new BehaviorSubject<any[]>([]);
  public productTags$: Observable<any> = this.dataSubjectProductTags.asObservable();

  private dataSubjectCompany = new BehaviorSubject<any[]>([]);
  public companyDetails$: Observable<any> = this.dataSubjectCompany.asObservable();

  currentCompanySelection = new BehaviorSubject<any>({});
  currentCompanySelection$ = this.currentCompanySelection.asObservable();

  private pendingEntityIds = new Set<string>();


  constructor(
    private appGlobals: AppGlobals, private http: HttpClient) { }

  ngOnInit(): void {
    if (localStorage.getItem('entities')) {
      this.entitiesSubject = new BehaviorSubject(JSON.parse(localStorage.getItem('entities')));
    } else {
      this.entitiesSubject = new BehaviorSubject([]);
    }
  }

  setInitialEntities(companies, user) {
    let entityList = [];
    // Safe fallback to empty array if companies is {} or not an array
    const safeCompanies = Array.isArray(companies) ? companies : [];
    safeCompanies.forEach((company) => {

      company.outlets?.forEach((outlet) => {
        entityList.push({
          id: outlet.id,
          group: company.name, // this will group all outlets under company
          name: outlet.name,
          parent: company.id,
          isSelected: false,
          parentId: company.id,
          maxInvoicePageCount: outlet.maxInvoicePageCount ?? company.maxInvoicePageCount,
          subscriptionDate: outlet.subscriptionDate,
          isPaidCompany: outlet.isPaidCompany,
          restaurantSyncCode: outlet.restaurantSyncCode || null,
          createdAt: outlet.createdAt || outlet.created_at || ''
        });
      });
    });

    this.setEntities(sortEntitiesWithinCompanies(entityList));
    this.setInitialCompanies(companies);
    return;
  }

  replaceDropdownFromCompanyList(companies: any[]) {
    if (!Array.isArray(companies) || !companies.some(company => company?.id && company?.name)) {
      return;
    }

    const previousEntities = new Map<string, any>((this.getEntities() || []).map((entity: any) => [entity.id, entity]));
    const previousCompanies = new Map<string, any>((this.getSingleDropdownCompanies() || []).map((company: any) => [company.id, company]));
    // A parent row is a real assigned company, not just the group header. Managers
    // receive it from company/list with parent null; owners already get it as a self-outlet.
    const entityRows = companies.filter(company => company?.id && company?.name);
    const knownGroup = (parentId: string) => [...previousEntities.values()].find(entity => entity.parentId === parentId)?.group;
    const parentLabels = new Map<string, string>();
    const labelFor = (company: any) => {
      const id = company.parent || company.id;

      if (!parentLabels.has(id)) {
        parentLabels.set(
          id,
          company.parentName || company.parent_name || knownGroup(company.parent) || previousCompanies.get(id)?.name || company.name
        );
      }

      return parentLabels.get(id);
    };
    const entities = entityRows.map(company => {
      const previous = previousEntities.get(company.id);
      const parentName = labelFor(company);

      return {
        id: company.id,
        name: company.name,
        group: parentName,
        parent: company.parent || company.id,
        parentId: company.parent || company.id,
        isSelected: !!previous?.isSelected,
        disabled: previous?.disabled,
        subscriptionDate: company.subscriptionDate,
        isPaidCompany: company.paymentStatus === 'PAID' || company.isPaidCompany || previous?.isPaidCompany,
        restaurantSyncCode: company.restaurantSyncCode || null,
        maxInvoicePageCount: company.maxInvoicePageCount,
        createdAt: company.createdAt || company.created_at || ''
      };
    });
    const dropdownCompanies = new Map<string, any>();

    companies.forEach(company => {
      const id = company.parent || company.id;
      const previous = previousCompanies.get(id);
      const parentName = labelFor(company);

      if (!dropdownCompanies.has(id)) {
        dropdownCompanies.set(id, {
          id,
          name: company.parent ? parentName : company.name,
          group: company.parent ? parentName : company.name,
          isSelected: !!previous?.isSelected,
          subscriptionDate: company.subscriptionDate || previous?.subscriptionDate,
          isPaidCompany: company.paymentStatus === 'PAID' || company.isPaidCompany || previous?.isPaidCompany,
          restaurantSyncCode: company.restaurantSyncCode || previous?.restaurantSyncCode || null
        });
      }
    });

    this.setSingleDropdownCompanies([...dropdownCompanies.values()].filter(company => company.name));
    this.setEntities(sortEntitiesWithinCompanies(entities));
  }

  setInitialCompanies(companies) {
    let companyList = [];
    const safeCompanies = Array.isArray(companies) ? companies : [];
    safeCompanies.forEach((company) => {
      if (!company.parent) {
        companyList.push({
          id: company.id,
          group: company.name,
          name: company.name,
          isSelected: false,
          isPaidCompany: company.isPaidCompany,
          subscriptionDate: company.subscriptionDate,
        })
      }
    });
    this.setSingleDropdownCompanies(companyList);
  }

  setEntities(entities) {
    localStorage.setItem('entities', JSON.stringify(entities));
    this.entitiesSubject?.next(entities);
  }

  setEntitiesTDC(entities) {
    localStorage.setItem('entitiesTDC', JSON.stringify(entities));
    this.entitiesSubjectTDC?.next(entities);
  }

  getEntities(): any {
    if (this.entitiesSubject?.value?.length) {
      return this.entitiesSubject.value;
    }

    if (localStorage.getItem('entities')) {
      return JSON.parse(localStorage.getItem('entities'));
    }

    return [];
  }

  getSingleDropdownCompanies() {
    if (this.singleDropdownCompanies?.value?.length) {
      return this.singleDropdownCompanies?.value;
    }

    if (localStorage.getItem('singleDropdownCompanies')) {
      return JSON.parse(localStorage.getItem('singleDropdownCompanies'));
    }

    return [];
  }

  getSingleSelectedCompany() {
    if (this.singleSelectedCompany?.value?.length) {
      return this.singleSelectedCompany?.value;
    }

    if (localStorage.getItem('singleSelectedCompany')) {
      return JSON.parse(localStorage.getItem('singleSelectedCompany'));
    }

    return null;
  }

  setSelectedEntities(entities) {
    localStorage.setItem('selectedEntities', JSON.stringify(entities));
    const isInvoicePage = typeof window !== 'undefined' && (window.location.pathname.endsWith('/invoice') || window.location.pathname.endsWith('/invoice/'));
    if (isInvoicePage) {
      localStorage.setItem('invoiceListSelectedEntities', JSON.stringify(entities));
    }
    this.selectedEntities.next(entities);
    this.cacheAdditionalOtherCharges(entities);
  }

  private cacheAdditionalOtherCharges(entities: any[]) {
    if (!entities || !entities.length) return;

    let cachedCharges: any = {};
    try {
      const stored = localStorage.getItem('entityAdditionalOtherCharges');
      if (stored) {
        cachedCharges = JSON.parse(stored);
      }
    } catch (e) { }

    // Only fetch Petpooja other-charges for entities that have a restaurant sync
    // code. Calling the API for unconfigured companies produces an expected 400
    // and previously threw in the backend (Sentry KHARCHA-BACKEND-KB).
    const uncachedEntityIds = entities
      .filter(e => !!e.restaurantSyncCode)
      .map(e => e.id)
      .filter(id => id && id !== 0 && id !== '0' && !cachedCharges.hasOwnProperty(id) && !this.pendingEntityIds.has(id));

    if (uncachedEntityIds.length === 0) return;

    uncachedEntityIds.forEach(id => this.pendingEntityIds.add(id));

    const requests$ = uncachedEntityIds.map(id => {
      const url = `${this.appGlobals.apiUrl.pushtopetpooja}petpooja-configured-charges?company=${id}`;
      return this.http.get<any>(url).pipe(
        catchError(() => of(null)), // Handle individual failures gracefully
        map(response => ({ id, response: response || [] }))
      );
    });

    forkJoin(requests$).subscribe(results => {
      let updated = false;
      results.forEach(result => {
        this.pendingEntityIds.delete(result.id);
        if (result.response) {
          cachedCharges[result.id] = result.response;
          updated = true;
        }
      });
      if (updated) {
        localStorage.setItem('entityAdditionalOtherCharges', JSON.stringify(cachedCharges));
      }
    });
  }

  setSingleSelectedEntity(entity) {
    localStorage.setItem('singleSelectedEntity', JSON.stringify(entity));
    this.singleSelectedEntity.next(entity);
  }

  setSingleSelectedCompany(company) {
    localStorage.setItem('singleSelectedCompany', JSON.stringify(company));
    this.singleSelectedCompany.next(company);
  }

  setSingleDropdownCompanies(companies) {
    localStorage.setItem('singleDropdownCompanies', JSON.stringify(companies));
    this.singleDropdownCompanies.next(companies);
  }

  setSelectedEntitiesTDC(entities) {
    localStorage.setItem('selectedEntitiesTDC', JSON.stringify(entities));
    this.selectedEntitiesTDC.next(entities);
  }

  getSelectedEntities() {
    const isInvoicePage = typeof window !== 'undefined' && (window.location.pathname.endsWith('/invoice') || window.location.pathname.endsWith('/invoice/'));
    if (isInvoicePage && localStorage.getItem('invoiceListSelectedEntities')) {
      return JSON.parse(localStorage.getItem('invoiceListSelectedEntities'));
    }
    if (this.selectedEntities?.value?.length) {
      return this.selectedEntities.value;
    } else if (localStorage.getItem('selectedEntities')) {
      return JSON.parse(localStorage.getItem('selectedEntities'));
    }
    return [];
  }

  getSelectedCompanyForKYC() {
    const selectedEntities = this.getSelectedEntities();
    if (selectedEntities?.length) {
      return selectedEntities[0]?.parent;
    }

  }

  getSelectedEntitiesTDC() {
    if (this.selectedEntitiesTDC.value.length) {
      return this.selectedEntitiesTDC.value;
    } else if (localStorage.getItem('selectedEntitiesTDC')) {
      return JSON.parse(localStorage.getItem('selectedEntitiesTDC'));
    }
    return [];
  }

  getSingleSelectedEntity() {
    if (this.singleSelectedEntity.value) {
      return this.singleSelectedEntity.value;
    } else if (localStorage.getItem('singleSelectedEntity')) {
      return JSON.parse(localStorage.getItem('singleSelectedEntity'));
    }
  }

  setLastSelectedEntities(entities) {
    localStorage.setItem('lastSelectedEntities', JSON.stringify(entities));
  }

  setLastSelectedEntitiesTDC(entities) {
    localStorage.setItem('lastSelectedEntitiesTDC', JSON.stringify(entities));
  }

  getLastSelectedEntities() {
    return JSON.parse(localStorage.getItem('lastSelectedEntities'));
  }

  getLastSelectedEntitiesTDC() {
    return JSON.parse(localStorage.getItem('lastSelectedEntitiesTDC'));
  }

  setLastSelectedCompany(company) {
    localStorage.setItem('lastSelectedCompany', JSON.stringify(company));
  }

  setLastSelectedCompanyTDC(company) {
    localStorage.setItem('lastSelectedCompanyTDC', JSON.stringify(company));
  }

  getLastSelectedCompany() {
    return JSON.parse(localStorage.getItem('lastSelectedCompany'));
  }

  getLastSelectedCompanyTDC() {
    return JSON.parse(localStorage.getItem('lastSelectedCompanyTDC'));
  }

  setEntitiesChanges(entityChange) {
    this.entitiesChanges.next(entityChange);
  }

  setEntitiesChangesTDC(entityChange) {
    this.entitiesChangesTDC.next(entityChange);
  }

  // fetchEntities(): Observable<OEntity[]> {
  //   return this.http.get<any>(`${this.appGlobals.apiUrl.metadata}entity/list`).pipe(
  //     map((entities: IEntity[]) => entities.map((entity: IEntity) => new OEntity(entity)))
  //   );
  // }
  fetchEntities(): Observable<any[]> {
    return this.http.get<any>(`${this.appGlobals.apiUrl.metadata}entity/list`);
  }

  companyUser(): Observable<any[]> {
    return this.http.get<any>(`${this.appGlobals.apiUrl.metadata}company/user`);
  }

  setCompanies(queryParams?: any): Observable<any[]> {
    if (!queryParams?.name) {
      const searchCompany = localStorage.getItem('searchCompany');
      if (searchCompany) {
        queryParams = { name: searchCompany }
      }
    }

    return this.http.get<any[]>(`${this.appGlobals.apiUrl.metadata}company/user`, {
      params: queryParams
    });
  }

  fetchCompanies() {
    this.setCompanies().subscribe({
      next: (response: any) => {
        if (response.company?.length) {
          this.dataSubject.next(response)
        } else {
          localStorage.removeItem('searchCompany');
          this.setCompanies().subscribe({
            next: (response: any) => {
              if (response.company?.length) {
                this.dataSubject.next(response)
              }
            }
          });
        }
      }
    });
  }

  getCompanies(): any[] {
    return this.dataSubject.value;
  }

  setProductTags(data) {
    this.dataSubjectProductTags.next(data)
  }

  setCompanyDetails(data) {
    this.dataSubjectCompany.next(data)
  }

  setCurrentCompanySelection(data) {
    this.currentCompanySelection.next(data);
    localStorage.setItem('currentCompanySelection', JSON.stringify(data));
  }

  getCurrentCompanySelection() {
    if (this.currentCompanySelection.value && Object.keys(this.currentCompanySelection.value).length) {
      return Array.isArray(this.currentCompanySelection.value)
        ? this.currentCompanySelection.value
        : [this.currentCompanySelection.value];
    } else if (localStorage.getItem('currentCompanySelection')) {
      const stored = JSON.parse(localStorage.getItem('currentCompanySelection'));
      return Array.isArray(stored) ? stored : [stored];
    } else {
      return [];
    }

  }

}
